import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "@/components/Button/Button";
import NoDataAvailable from "@/components/NoDataAvailable/NoDataAvailable";
import Loader from "@/components/Loader/Loader";
import {
  BackArrowIcon,
  BreadCrumbChevronIcon,
  CalendarMiniIcon,
  HistoryMiniIcon,
} from "@/components/SVGComponents/MemberIcons";
import { PROFILE_BASE_PATH, PROFILE_CHILD_PATHS } from "@/pages/Profile/profilePaths";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchUserSubscription } from "@/store/slices/subscriptionSlice";
import {
  formatMembershipDate,
  formatPrice,
  getDurationLabel,
  getQueuedPlan,
  hasActiveSubscription,
  isQueuedOnlySubscription,
} from "@/pages/Profile/Subscription/subscriptionFormatters";
import styles from "./Subscription.module.scss";

const billingHistoryPath = `${PROFILE_BASE_PATH}/${PROFILE_CHILD_PATHS.BILLING_HISTORY}`;
const manageMembershipPath = `${PROFILE_BASE_PATH}/${PROFILE_CHILD_PATHS.MANAGE_MEMBERSHIP}`;

const Subscription = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSubscription, isLoadingCurrent, error } = useAppSelector(
    (state) => state.subscription,
  );

  useEffect(() => {
    dispatch(fetchUserSubscription());
  }, [dispatch]);

  const hasActivePlan = hasActiveSubscription(currentSubscription);
  const queuedPlan = getQueuedPlan(currentSubscription);
  const isQueuedOnly = isQueuedOnlySubscription(currentSubscription);
  const hasSubscriptionData = hasActivePlan || Boolean(queuedPlan);
  const isLoading = isLoadingCurrent;
  const displayPlan = isQueuedOnly && queuedPlan ? queuedPlan : currentSubscription;
  const showQueuedSection = Boolean(queuedPlan) && hasActivePlan;

  const renderContent = () => {
    if (isLoading && !currentSubscription) {
      return (
        <div className={styles.loaderWrap}>
          <Loader fullscreen={false} size={96} />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorMessage}>
          <p>{error}</p>
        </div>
      );
    }

    if (!hasSubscriptionData || !displayPlan) {
      return (
        <div className={styles.emptyStateWrap}>
          <NoDataAvailable
            description="Your current plan details will appear here once your membership is active."
            note="Membership details stay in sync here so you can track your active plan in one place."
            title="No subscription details available"
          />
        </div>
      );
    }

    const statusClassName =
      displayPlan?.status?.toLowerCase() === "queued"
        ? styles.active
        : styles[displayPlan?.status?.toLowerCase() ?? ""];

    return (
      <>
        <div className={styles.content}>
          <article className={styles.activePlanCard}>
            <div className={styles.activePlanHeader}>
              <h2>{displayPlan?.name}</h2>
              <span className={`${styles.statusBadge} ${statusClassName}`}>
                {displayPlan?.status}
              </span>
            </div>

            <div className={styles.priceRow}>
              <strong>₹ {formatPrice(displayPlan?.amount)}</strong>
              <span>{getDurationLabel(displayPlan?.durationMonth)}</span>
            </div>

            {displayPlan?.description ? (
              <p className={styles.planDescription}>{displayPlan?.description}</p>
            ) : null}
          </article>

          {showQueuedSection ? (
            <section className={styles.queuedSection}>
              <div className={styles.sectionHeading}>
                <h3>Queued Plan</h3>
                <p>You have this plan in queue</p>
              </div>

              <article className={styles.queuedCard}>
                <div className={styles.queuedPlanRow}>
                  <span className={styles.queuedPlanName}>{queuedPlan?.name}</span>
                  <strong>₹ {formatPrice(queuedPlan?.amount)}</strong>
                </div>
                <p className={styles.queuedNote}>
                  Note: This plan will activate after your current plan ends.
                </p>
              </article>
            </section>
          ) : null}

          <section className={styles.detailsSection}>
            <h3 className={styles.detailsTitle}>Membership Details</h3>

            <div className={styles.detailsList}>
              <div className={styles.detailRow}>
                <div className={styles.detailLeft}>
                  <span className={styles.detailIcon}>
                    <CalendarMiniIcon />
                  </span>
                  <span className={styles.detailLabel}>Started on</span>
                </div>
                <strong>{formatMembershipDate(displayPlan?.startDate)}</strong>
              </div>

              <div className={styles.detailRow}>
                <div className={styles.detailLeft}>
                  <span className={styles.detailIcon}>
                    <HistoryMiniIcon />
                  </span>
                  <span className={styles.detailLabel}>Next Billing</span>
                </div>
                <strong>{formatMembershipDate(displayPlan?.endDate)}</strong>
              </div>
            </div>
          </section>
        </div>

        <div className={styles.actionBar}>
          <Button
            large
            onClick={() => navigate(manageMembershipPath)}
            type="button"
          >
            Manage Membership
          </Button>
          <Button
            large
            onClick={() => navigate(billingHistoryPath)}
            outlined
            type="button"
          >
            Billing history
          </Button>
        </div>
      </>
    );
  };

  return (
    <section className={styles.page}>
      <div className={styles.orbLeft} />
      <div className={styles.orbRight} />
      <div className={styles.dotsRight} />

      <div className={styles.inner}>
        <div className={styles.mobileHeader}>
          <Link aria-label="Back to profile" className={styles.backButton} to={PROFILE_BASE_PATH}>
            <BackArrowIcon />
          </Link>
          <h1>Membership</h1>
          <span className={styles.headerSpacer} />
        </div>

        <div className={styles.breadcrumbs}>
          <Link className={styles.breadcrumbLink} to={PROFILE_BASE_PATH}>
            Profile
          </Link>
          <BreadCrumbChevronIcon />
          <span className={styles.activeCrumb}>Membership</span>
        </div>

        {renderContent()}
      </div>
    </section>
  );
};

export default Subscription;
